import React from "react";
import { renderDate } from "../../../formatting/dateFormat";
import { hourlyOrDailyDateDifference } from "../../../formatting/dateDifferHoursDaily";
import { formatToPoundsOunces } from "../../../formatting/weightFormat";
import {
  GridRenderCellParams,
  GridActionsCellItem,
  GridRowParams,
  GridEditInputCell,
} from "@mui/x-data-grid-premium";
import { ExtendedGridColDef } from "../../../components/datagrid/overrides";
import BigNumber from "bignumber.js";
import { HtmlCell } from "../../../components/datagrid/renderers/HtmlCell";
import { ExpandButton } from "../../../components/datagrid/components/ExpandButton";
import { trimWithEllipses } from "../../../components/datagrid/formatting";
import {
  getMultiSelectFilterOperators,
  MultiSelectCell,
  MultiSelectEditCell,
} from "../../../components/datagrid/MultiSelectCell";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { BuyBoxInfoCell } from "./customRenderCellComponents/BuyBoxInfoCell";
import BuyboxInfoLegend from "./tooltips/BuyboxInfo";
import BuyboxMarkupLegend from "./tooltips/BuyboxMarkup";
import { getBuyBoxInfoFilterOperators } from "../../../components/datagrid/BuyBoxInfoFilter";

const renderEditCellCursorStart = (params: any) => (
  <GridEditInputCell
    {...params}
    inputProps={{
      ...(params.inputProps ?? {}),
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        params.inputProps?.onFocus?.(event);
        event.target.setSelectionRange(0, 0);
      },
    }}
  />
);

export const columns: ExtendedGridColDef[] = [
  {
    field: "listing_associated_inventory_count_view.associated_inventory_count",
    headerName: "",
    description: "",
    type: "actions",
    hideable: false,
    width: 50,
    getActions: (params: GridRowParams) => {
      const associatedCount =
        params.row.listing_associated_inventory_count_view
          ?.associated_inventory_count ?? 0;

      // Only show expand button if there are associated inventory items
      if (associatedCount < 1) {
        return [];
      }

      return [<ExpandButton key="expand" rowId={params.id} />];
    },
  },
  {
    field: "product.sort",
    headerName: "Sort",
    description: "User designated sort position",
    type: "number",
    valueGetter: (value, row) => row.product?.sort,
    editable: true,
  },
  {
    field: "product.msku",
    headerName: "MSKU",
    description: "MSKU (Merchant Stock Keeping Unit)",
    valueGetter: (value, row) => row.product?.msku,
    editable: true,
  },
  {
    field: "product.product_asin",
    headerName: "ASIN",
    description: "Product ASIN (Amazon Standard Identification Number)",
    valueGetter: (value, row) => row.product?.product_asin,
    editable: true,
  },
  {
    field: "product.html",
    headerName: "HTML",
    description: "General HTML column for row",
    valueGetter: (value, row) => row.product?.html,
    renderCell: (params: GridRenderCellParams) => (
      <HtmlCell
        value={params.value}
        containerStyle={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />
    ),
    editable: true,
  },
  {
    field: "product.product_title",
    headerName: "Title",
    description: "Yom 's title for item (not Amazon 's)",
    width: 350,
    disableAutosize: true,
    valueGetter: (value, row) => row.product?.product_title,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "clip",
            lineHeight: "1.4",
            wordBreak: "break-word",
          }}
        >
          {params.value}
        </div>
      );
    },
    editable: true,
  },
  {
    field: "product_families_view.families",
    headerName: "Families",
    description: "Product families this product is a member of.",
    width: 250,
    disableAutosize: true,
    valueGetter: (value, row) => row.product_families_view?.families,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "product.assembly_part_number",
    headerName: "Part Number",
    description: "Parts required to assemble this product",
    valueGetter: (value, row) => row.product?.assembly_part_number,
  },
  {
    field: "product.legacy_part_number",
    headerName: "Legacy Part Number",
    description:
      "Legacy part number from old system to act as guiding reference with new release",
    valueGetter: (value, row) => row.product?.legacy_part_number,
  },
  {
    field: "product.packaged_weight_ounces",
    headerName: "Weight",
    description: "Weight of the product in lbs and ounces",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      return formatToPoundsOunces(params.value);
    },
    valueGetter: (value, row) => row.product?.packaged_weight_ounces,
  },
  {
    field: "product.component_count",
    headerName: "Part Qty",
    description: "How many components are in this product",
    type: "number",
    valueGetter: (value, row) => row.product?.component_count,
    editable: true,
  },
  {
    field: "product.yom_handling_cost_adjustment",
    headerName: "Handling Cost Adjustment",
    description: "Custom user inputted amount to adjust base handling cost",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_handling_cost_adjustment,
    editable: true,
  },
  {
    field: "product.genre",
    headerName: "Genre",
    description: "Genre",
    valueGetter: (value, row) => row.product?.genre,
    editable: true,
  },
  {
    field: "media_types",
    headerName: "Media Types",
    description:
      "The media types in this product (Vinyl, Book, CD, DVD, Bluray, 4K)",
    type: "custom",
    valueGetter: (value, row) => row.media_types,
    valueFormatter: (value: { label: string }[] | null | undefined) =>
      Array.isArray(value) ? value.map((v) => v.label).join(", ") : (value ?? ""),
    renderCell: (params: GridRenderCellParams) => (
      <MultiSelectCell {...params} />
    ),
    renderEditCell: (params) => <MultiSelectEditCell {...params} />,
    filterOperators: getMultiSelectFilterOperators("media_types"),
    editable: true,
  },
  {
    field: "product.retired",
    headerName: "Retired",
    description: "Whether or not the product has been retired.",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 0) return "No";
      if (!data) return null;
      return "Yes";
    },
    valueGetter: (value, row) => row.product?.retired,
  },
  {
    field: "product.retired_timestamp",
    headerName: "Retired Timestamp",
    description: "When the product was retired.",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.retired_timestamp
        ? new Date(row.product?.retired_timestamp)
        : null,
  },
  {
    field: "product.ship_weight",
    headerName: "Ship Wt",
    description: "Weight and Dimensions of a collection",
    valueGetter: (value, row) => row.product?.ship_weight,
  },
  {
    field: "product.part_number",
    headerName: "Part #",
    description:
      "Identifier for parents of a collection (.amx / .rmx / .mx / .bmx) ",
    valueGetter: (value, row) => row.product?.part_number,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "product.card_number",
    headerName: "Card#",
    description: "The artcard number for this collection",
    valueGetter: (value, row) => row.product?.card_number,
    editable: true,
  },
  {
    field: "product.total_sales",
    headerName: "Total UPC Sales",
    description:
      "The total units of this UPC sold on Amazon over the product's lifetime.",
    type: "number",
    valueGetter: (value, row) => row.product?.total_sales,
  },
  {
    field: "product.90_day_sales",
    headerName: "90 Day UPC Sales",
    description: "Units of this UPC sold on Amazon over the past 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["90_day_sales"],
  },
  {
    field: "product.30_day_sales",
    headerName: "30 Day UPC Sales",
    description: "Units of this UPC sold on Amazon over the past 30 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["30_day_sales"],
  },
  {
    field: "product.7_day_sales",
    headerName: "7 Day UPC Sales",
    description: "Units of this UPC sold on Amazon over the past 7 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["7_day_sales"],
  },
  {
    field: "product.ats",
    headerName: "ATS",
    description: "Quantity of product available to ship",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.ats_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.ats,
  },
  {
    field: "product.ats_last_increase_timestamp",
    headerName: "ATS Increased",
    description: "Timestamp for when the ATS value last increased",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.ats_last_increase_timestamp
        ? new Date(row.product?.ats_last_increase_timestamp * 1000)
        : null,
  },
  {
    field: "product.master_analyst_inv",
    headerName: "MAI",
    description:
      "Master Analyst Inventory. Current inventory on hand + Inventory On Order (ONO) for all UPCs required to fulfill this row.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mai_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_analyst_inv,
  },
  {
    field: "product.restock_status_calculated",
    headerName: "Restock",
    description:
      "The availability of supply inventory needed to fulfill this selling option from our distributors. IS=In stock, BO=Back Order, OP=Out of Print",
    type: "string",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === "OP")
        return (
          <span
            style={{ color: "#CC0000", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            OP
          </span>
        );
      if (data === "BO")
        return (
          <span
            style={{ color: "#0066FF", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            BO
          </span>
        );
      if (data === "IS")
        return (
          <span
            style={{ color: "#339933", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            IS
          </span>
        );

      return null;
    },
    valueGetter: (value, row) => row.product?.restock_status_calculated,
  },
  {
    field: "product.weeks_until_depleted",
    headerName: "WTD",
    description:
      "Weeks to Depletion. How many weeks of stock do we have on hand or purchased to fulfill this selling option, based on the last 30 days of total page sales?",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 9999999.9) return "No sales.";
      if (data === 9999999.8) return "Inventory error.";
      return data;
    },
    valueGetter: (value, row) => row.product?.weeks_until_depleted,
  },
  {
    field: "product.amazon_stock_weeks_until_depleted",
    headerName: "WTD AF",
    description:
      "How many weeks of stock do we have in Amazon's network to fulfill this selling option, based on the last 30 days of total page sales?",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 9999999.9) return "No sales.";
      if (data === 9999999.8) return "Inventory error.";
      return data;
    },
    valueGetter: (value, row) => row.product?.amazon_stock_weeks_until_depleted,
  },
  {
    field: "product.replacement_cost",
    headerName: "MRCOG-P",
    description:
      "Master replacement cost of goods - purchasable. The lowest possible cost of purchasing all of the supply inventory required to fulfill this selling option, from any suppliers.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.rcog_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.replacement_cost,
  },
  {
    field: "product.replacement_cost_dist",
    headerName: "MRCOG-IS",
    description:
      "The lowest cost of purchasing all of the supply inventory required to fulfill this selling option, which is in stock with a distributor supplier.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.rcog_dist_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.replacement_cost_dist,
  },
  {
    field: "product.master_avg_cost_of_goods",
    headerName: "MAVCG",
    description:
      "The sum of the average cost of all in-stock components needed to fulfill this collection.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mavcg_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_avg_cost_of_goods,
  },
  {
    field: "product.master_quantity_on_order",
    headerName: "MONO",
    description:
      "Master On Order. Current inventory On Order (ONO) for all UPCs required to fulfill this row.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mono_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_quantity_on_order,
  },
  {
    field: "product_listings.merchant_available",
    headerName: "Assoc",
    description:
      "Is there a merchant listing available for the ASIN of this row?",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 0) return "No";
      if (!data) return null;
      return "Yes";
    },
    valueGetter: (value, row) => row.product_listings?.merchant_available,
  },
  {
    field: "product_listings.purchasable_stock",
    headerName: "Page Stock",
    description: "The total purchasable units for the ASIN's listing page.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.purchasable_stock,
  },
  {
    field: "product_listings.30_day_profit",
    headerName: "30 Day Profit",
    description: "The total profit of this ASIN's last 30 day sales.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      const numericUsd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);

      return numericUsd;
    },
    valueGetter: (value, row) => row.product_listings?.["30_day_profit"],
  },
  {
    field: "product.page_sales_365_90_30_7",
    headerName: "All Page Sales",
    description: "Page sales in the following format: 365 - 90 - 30 - 7.",
    valueGetter: (value, row) => row.product?.page_sales_365_90_30_7,
  },
  {
    field: "product_listings.total_sales",
    headerName: "Total Page Sales",
    description:
      "The lifetime aggregate sales for YOM's products on this ASIN listing page.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.total_sales,
  },
  {
    field: "product_listings.365_day_sales",
    headerName: "365 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 365 days.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.["365_day_sales"],
  },
  {
    field: "product_listings.90_day_sales",
    headerName: "90 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.["90_day_sales"],
  },
  {
    field: "product_listings.30_day_sales",
    headerName: "30 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 30 days.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.["30_day_sales"],
  },
  {
    field: "product_listings.7_day_sales",
    headerName: "7 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 7 days.",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.["7_day_sales"],
  },
  {
    field: "product_listings.open_date",
    headerName: "Listing Created Date",
    description: "Date that this listing was created",
    type: "date",
    valueGetter: (value, row) =>
      row.product_listings?.open_date
        ? new Date(row.product_listings?.open_date)
        : null,
  },
  {
    field: "amazon_product_pricing_info.sales_rank",
    headerName: "Sales Rank",
    description: "Sales rank on Amazon's website",
    valueGetter: (value, row) => row.amazon_product_pricing_info?.sales_rank,
  },
  {
    field: "amazon_product_pricing_info.product_category",
    headerName: "Product Category",
    description: "",
    valueGetter: (value, row) =>
      row.amazon_product_pricing_info?.product_category,
  },
  {
    field: "amazon_product_pricing_info.buy_box_price",
    headerName: "BuyBox",
    description: "The price that is currently listed under the buy box",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      const numericUsd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);

      return numericUsd;
    },
    valueGetter: (value, row) => row.amazon_product_pricing_info?.buy_box_price,
  },
  {
    field: "amazon_product_pricing_info.buy_box_info",
    headerName: "Buy Box Info",
    description:
      "Information about the buybox seller, ship time, and various other listing conditions.",
    type: "custom",
    renderHeader: () => <BuyboxInfoLegend />,
    renderCell: BuyBoxInfoCell,
    filterOperators: getBuyBoxInfoFilterOperators(),
    valueGetter: (value, row) => {
      return row.amazon_product_pricing_info?.buy_box_info;
    },
  },
  {
    field: "product.buy_box_markup_display",
    headerName: "Buybox Markup",
    description:
      "Percentage markup/markdown relative to children/equivalent products",
    renderHeader: () => <BuyboxMarkupLegend />,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const error = params.row.product?.buy_box_markup_error;
      const missingPieces = params.row.product?.buy_box_markup_missing_pieces;

      if (!data && !error && !missingPieces) return null;

      if (error === "Misthread") {
        return "Misthread";
      }

      if (error === "Ineligible") {
        return (
          <b>
            <span style={{ fontSize: "2em" }}> &#8734;</span>
          </b>
        );
      }

      if (error === "Not applicable") {
        return (
          <span style={{ color: "Purple" }}>
            <b>
              <span style={{ fontSize: "1.25em" }}>N/A</span>
            </b>
          </span>
        );
      }

      if (missingPieces) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <img
              src="https://i.ibb.co/Nsdhbrz/1.png"
              style={{ height: "30px" }}
              alt="image"
            />{" "}
            <b style={{ fontSize: "20px" }}>{missingPieces}</b>
          </div>
        );
      }

      if (data === null) return null;

      const percentage = data.match(/[0-9]+/);

      if (data.includes("Premium")) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <b>+{percentage[0]}%</b>
            <img
              src="https://i.ibb.co/m9Dj2FY/Green-Arrow.png"
              style={{ color: "green" }}
              alt="Premium"
            />
          </div>
        );
      }

      if (data.includes("Discount")) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <b>{percentage[0]}%</b>
            <img
              src="https://i.ibb.co/pXbvMqQ/Red-Arrow.png"
              style={{ color: "green" }}
              alt="Discount"
            />
          </div>
        );
      }

      if (data.includes("PAR")) {
        return <b>PAR</b>;
      }

      return data;
    },
    valueGetter: (value, row) => row.product?.buy_box_markup_display,
  },
  {
    field: "fba_latest_shipment.created_at",
    headerName: "FBA Last Ship Date",
    description: "Date of the latest non-error FBA shipment for this MSKU",
    valueGetter: (value, row) => row.fba_latest_shipment?.created_at,
    valueFormatter: (value) => renderDate(value),
    type: "dateTime",
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) return null;
      const shipmentId = params.row.fba_latest_shipment?.shipment_id;
      const href = shipmentId
        ? `/datatables/fba-shipments?shipments_filter_fba_shipments.shipment_id_operator=equals&shipments_filter_fba_shipments.shipment_id_value=${shipmentId}`
        : null;
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {renderDate(params.value)}
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon
                sx={{
                  fontSize: 13,
                  verticalAlign: "middle",
                  color: "action.active",
                }}
              />
            </a>
          )}
        </span>
      );
    },
  },
  {
    field: "fba_latest_shipment.quantity",
    headerName: "FBA Last Ship Qty",
    description: "Quantity in the latest non-error FBA shipment for this MSKU",
    type: "number",
    valueGetter: (value, row) => row.fba_latest_shipment?.quantity,
    renderCell: (params: GridRenderCellParams) => {
      if (params.value == null) return null;
      const shipmentId = params.row.fba_latest_shipment?.shipment_id;
      const href = shipmentId
        ? `/datatables/fba-shipments?shipments_filter_fba_shipments.shipment_id_operator=equals&shipments_filter_fba_shipments.shipment_id_value=${shipmentId}`
        : null;
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {params.value}
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon
                sx={{
                  fontSize: 13,
                  verticalAlign: "middle",
                  color: "action.active",
                }}
              />
            </a>
          )}
        </span>
      );
    },
  },
  {
    field: "product.wn",
    headerName: "WN",
    description: "WN (Warehouse notes)",
    valueGetter: (value, row) => row.product?.wn,
    editable: true,
  },
  {
    field: "product.wwo",
    headerName: "WWO",
    description: "WWO (Warehouse work orders)",
    valueGetter: (value, row) => row.product?.wwo,
    editable: true,
  },
  {
    field: "product.listory",
    headerName: "Listory",
    description: "Significant events in the life of a product listing",
    valueGetter: (value, row) => row.product?.listory,
    editable: true,
  },
  {
    field: "product.pick_status",
    headerName: "Pick Status",
    description: "Pick Status",
    valueGetter: (value, row) => row.product?.pick_status,
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "product.master_avg_cogs_ninety_day",
    headerName: "MAVCG-H 90",
    description:
      "The master average cost of goods paid for the product using the AVCG-H 90 value.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mavcg_90_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_avg_cogs_ninety_day,
  },
  {
    field: "product.last_ninety_day_avg_cogs",
    headerName: "AVCG-H 90",
    description:
      "The average (mean) cost paid for the product in our warehouse in the last 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.last_ninety_day_avg_cogs,
  },
  {
    field: "product.one_year_lowest_cost",
    headerName: "One Year Lowest COGS",
    description:
      "The lowest cost paid for the product in our warehouse in the last year.",
    type: "number",
    valueGetter: (value, row) => row.product?.one_year_lowest_cost,
  },
  {
    field: "product.next_amz_ship_qty",
    headerName: "Next Ship Qty",
    description: "Next Shipment - number of units to be shipped",
    type: "number",
    valueGetter: (value, row) => row.product?.next_amz_ship_qty,
    editable: true,
  },
  {
    field: "product.ysr_nss_rec",
    headerName: "YSR NSS Rec",
    description: "Yom's new ship stock target value used in YSR holiday calc",
    type: "number",
    valueGetter: (value, row) => row.product?.ysr_nss_rec,
  },
  {
    field: "product.yom_purchase_rec",
    headerName: "YPR",
    description: "Yom's purchase recommendation",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_purchase_rec,
  },
  {
    field: "product.yom_ship_rec_holiday",
    headerName: "YSR Holiday",
    description: "Yom's recommended ship quantity scaled for holidays",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_ship_rec_holiday,
  },
  {
    field: "product.yom_ship_rec",
    headerName: "YSR",
    description: "Yom's recommended ship quantity",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_ship_rec,
  },
  {
    field: "product.amz_recommended_ship_quantity",
    headerName: "ASR",
    description: "Amazon's recommended ship quantity",
    type: "number",
    valueGetter: (value, row) => row.product?.amz_recommended_ship_quantity,
  },
  {
    field: "product.amz_ship_qty",
    headerName: "Amz Ship Qty",
    description:
      "ASQ (Amazon Ship Quantity) Number of units to be shipped to Amazon's warehouse network from our warehouse ",
    type: "number",
    valueGetter: (value, row) => row.product?.amz_ship_qty,
    editable: true,
  },
  {
    field: "product.yom_ship_rec_amz_ship_rec_diff",
    headerName: "YSR ASR Diff",
    description:
      "Difference between YOM ship recommendation and AMZ ship recommendation",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_ship_rec_amz_ship_rec_diff,
  },
  {
    field: "product_profit_projection_view.yom_ship_rec_potential_profit",
    headerName: "YSR Potential Profit",
    description: "Projected profit from selling YOM ship recommended quantity",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);
    },
    valueGetter: (value, row) =>
      row.product_profit_projection_view?.yom_ship_rec_potential_profit,
  },
  {
    field: "product.30_day_restock_in_stock_count",
    headerName: "30 Day IS Rate",
    description:
      "Total days with restock status = IS in the past 30 days. Reported as # days (%)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;

      const percentage = new BigNumber(data).dividedBy(30).multipliedBy(100);
      return `${data} (${percentage.toFixed(0)}%)`;
    },
    valueGetter: (value, row) => row.product?.["30_day_restock_in_stock_count"],
  },
  {
    field: "product.analytics_db_column_1",
    headerName: "Analytics DB 1",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.product?.analytics_db_column_1,
  },
  {
    field: "product.analytics_db_column_2",
    headerName: "Analytics DB 2",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.product?.analytics_db_column_2,
  },
  {
    field: "product.analytics_db_column_3",
    headerName: "Analytics DB 3",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.product?.analytics_db_column_3,
  },
  {
    field: "product.analytics_db_column_4",
    headerName: "Analytics DB 4",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.product?.analytics_db_column_4,
  },
  {
    field: "product.amz_queue",
    headerName: "Amazon Queue",
    description:
      "ASD (Amazon Ship Date) Date units must be shipped to Amazon's warehouse network from our warehouse",
    editable: true,
    width: 500,
    disableAutosize: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.product?.amz_queue,
  },
  {
    field: "product.orders",
    headerName: "Orders",
    description: "Marking column (General Use) ",
    width: 300,
    disableAutosize: true,
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value: any, row: any) => row.product?.orders,
  } as any,
  {
    field: "product.bcp",
    headerName: "BCP",
    description:
      "BCP (Barcode Printed) Whether or not the barcode has been printed for this product (ex. Yes, No)",
    valueGetter: (value, row) => row.product?.bcp,
    editable: true,
  },
  {
    field: "product.an",
    headerName: "AN",
    description: "AN (Analyst Notes) Notes for the analyst to use",
    valueGetter: (value, row) => row.product?.an,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "product.pricing_notes",
    headerName: "Pricing Notes",
    description: "Pricing Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.product?.pricing_notes,
  },
  {
    field: "product.threading_notes",
    headerName: "Threading Notes",
    description: "Threading Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.product?.threading_notes,
  },
  {
    field: "product.curation_notes",
    headerName: "Curation Notes",
    description: "Curation Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.product?.curation_notes,
  },
  {
    field: "product.inventory_notes",
    headerName: "Inventory Notes",
    description: "Inventory Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    valueGetter: (value, row) => row.product?.inventory_notes,
  },
  {
    field: "product.shelf",
    headerName: "Shelf",
    description: "Where the item can be found in our warehouse ",
    valueGetter: (value, row) => row.product?.shelf,
    editable: true,
  },
  {
    field: "product.alpha1",
    headerName: "Alpha 1",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.product?.alpha1,
    editable: true,
  },
  {
    field: "product.alpha2",
    headerName: "Alpha 2",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.product?.alpha2,
    editable: true,
  },
  {
    field: "product.priority",
    headerName: "Priority",
    description: "Priority Order for FBA Shipping Queue",
    valueGetter: (value, row) => row.product?.priority,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    editable: true,
  },
  {
    field: "product.num1",
    headerName: "Num 1",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.product?.num1,
    editable: true,
  },
  {
    field: "product.num2",
    headerName: "Num 2",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.product?.num2,
    editable: true,
  },
  {
    field: "product.num3",
    headerName: "Num 3",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.product?.num3,
    editable: true,
  },
  {
    field: "product.prod_list_type",
    headerName: "Product List Type",
    description: "Product List Type",
    valueGetter: (value, row) => row.product?.prod_list_type,
    editable: true,
  },
  {
    field: "product_listings.price",
    headerName: "Listing Price",
    description: "Your Online Marketplace's current listing price",
    type: "number",
    valueGetter: (value, row) => row.product_listings?.price,
  },
  {
    field: "product.fnsku",
    headerName: "FNSKU",
    description: "FNSKU (Amazon's Fulfillment Network SKU) ",
    valueGetter: (value, row) => row.product?.fnsku,
    editable: true,
  },
  {
    field: "product.stock",
    headerName: "Stock",
    description: "Purchasable quantity available on Amazon ",
    type: "number",
    valueGetter: (value, row) => row.product?.stock,
  },
  {
    field: "product.status",
    headerName: "Status",
    description:
      "Your Online Marketplace's listing status (F - FBA, M - Merchant, .A - Active, .Inactive, .Incomplete) ",
    valueGetter: (value, row) => row.product?.status,
  },
  {
    field: "product_listings.last_non_cancelled_order_timestamp",
    headerName: "Time Since Last Order",
    description:
      "How many days since Your Online Marketplace has sold this product ",
    type: "date",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      const today = new Date();
      const lastestOrderDate = new Date(data * 1000);
      return hourlyOrDailyDateDifference(today, lastestOrderDate);
    },
    valueGetter: (value, row) =>
      row.product_listings?.last_non_cancelled_order_timestamp
        ? new Date(row.product_listings?.last_non_cancelled_order_timestamp)
        : null,
  },
  {
    field: "product.ship_stock",
    headerName: "NSS",
    description:
      "New Shipped Stock = Available + FC Transfer + FC Processing + Working + Shipped + Receiving ",
    type: "number",
    valueGetter: (value, row) => row.product?.ship_stock,
  },
  {
    field: "product.inv_age_271_to_365",
    headerName: "Inv Age 271- 365",
    description:
      "Number of unsold units at Amazon's warehouse that have been there for more than 270 days",
    type: "number",
    valueGetter: (value, row) => row.product?.inv_age_271_to_365,
  },
  {
    field: "product.alert",
    headerName: "Alert",
    description:
      "Amazon's recommendation for fixing a poor preforming listing ",
    valueGetter: (value, row) => row.product?.alert,
  },
  {
    field: "product.fba_sell_through",
    headerName: "FBA Sell Thru",
    description:
      "Units sold and shipped over the past 90 days divided by the average number of units available at fulfillment centers during that time period. A higher number means we sell through inventory fast, and vice versa. ",
    type: "number",
    valueGetter: (value, row) => row.product?.fba_sell_through,
  },
  {
    field: "product.cogs",
    headerName: "COGS",
    description:
      "COGS (Cost of Goods Sold) Price Your Online Marketplace paid for our goods, inputed by a analyst",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.cogs,
    editable: true,
  },
  {
    field: "product.cogs_update_timestamp",
    headerName: "COGS Timestamp",
    description:
      "Shows the timestamp of the last time COGS was updated for a row.",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.cogs_update_timestamp
        ? new Date(row.product?.cogs_update_timestamp * 1000)
        : null,
  },
  {
    field: "product.type",
    headerName: "Type",
    description:
      "Representation if the product was repriced or prospected using our Amazon pricing calculator",
    valueGetter: (value, row) => row.product?.type,
  },
  {
    field: "product.margin",
    headerName: "Margin",
    description: "Profit margin percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `${data.toFixed(1)}%`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.margin,
  },
  {
    field: "product.profit",
    headerName: "UPC Profit",
    description:
      "Profit that we get if we were to sell the product (Selling Price - COGS - Amazon fees - Your Online Marketplace Warehouse & Handling fees)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.profit,
  },
  {
    field: "product.proceeds",
    headerName: "Proceeds",
    description:
      "Selling Price - Amazon fees directly attributable to the sale and fulfillment of an FBA order (inc. referral, fulfillment and closing fees)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.proceeds,
  },
  {
    field: "product.roi",
    headerName: "ROI",
    description:
      "ROI (Return on Investment) The gain or loss on an investment relative to the amount of money invested (COGS = Warehousing Fee)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `${data.toFixed(1)}%`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.roi,
  },
  {
    field: "product.margin_0_pricing",
    headerName: "List 0",
    description: "Selling Price required to achieve a 0% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.margin_0_pricing,
  },
  {
    field: "product.margin_15_pricing",
    headerName: "List 15",
    description: "Selling Price required to achieve a 15% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.margin_15_pricing,
  },
  {
    field: "product.margin_30_pricing",
    headerName: "List 30",
    description: "Selling Price required to achieve a 30% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.product?.margin_30_pricing,
  },
  {
    field: "keepa_product_data.keepa_buybox_30_day_avg",
    headerName: "Keepa 30d",
    description: "Keepa 30 day buybox average",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_buybox_30_day_avg,
  },
  {
    field: "keepa_product_data.keepa_30_day_sales_range",
    headerName: "Keepa 30d Sales",
    description: "Keepa 30 day sales range",
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_30_day_sales_range,
  },
  {
    field: "product.yom_market_share",
    headerName: "YOM Market Share",
    description: "YOM Market Share",
    valueGetter: (value, row) => row.product?.yom_market_share,
  },
  {
    field: "product.yom_buybox_30_day_avg",
    headerName: "YOM 30d Avg",
    description: "YOM 30 day average",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) => row.product?.yom_buybox_30_day_avg,
  },
  {
    field: "product.master_avg_replacement_cost_diff",
    headerName: "MAVCG - MRCOG-IS",
    description: "MAVCG - MRCOG-IS",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data !== null ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) => row.product?.master_avg_replacement_cost_diff,
  },
  {
    field: "keepa_product_data.keepa_timestamp",
    headerName: "Keepa TimeStamp",
    description: "When was the data pulled from Keepa API",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_timestamp
        ? new Date(row.keepa_product_data.keepa_timestamp * 1000)
        : null,
  },
  {
    field: "product.latest_timestamp",
    headerName: "Calculator TimeStamp",
    description: "When a product was last prospected or priced",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.latest_timestamp
        ? new Date(row.product?.latest_timestamp * 1000)
        : null,
  },
  {
    field: "product.created_at",
    headerName: "Created At",
    description: "When this product row was created in YOM's system",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.created_at ? new Date(row.product?.created_at) : null,
  },
  {
    field: "product.listing_purchase_recommendation",
    headerName: "LPR",
    description:
      "Listing Purchase Recommendation ((3 * 30 Day Sales) - MAI - New Ship Stock)",
    type: "number",
    valueGetter: (value, row) => row.product?.listing_purchase_recommendation,
  },
  {
    field: "product.short_term_demand_trend_percentage",
    headerName: "ST Demand %",
    description: "Listing Demand Short Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.short_term_demand_trend_percentage,
  },
  {
    field: "product.medium_term_demand_trend_percentage",
    headerName: "MT Demand %",
    description: "Listing Demand Medium Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.medium_term_demand_trend_percentage,
  },
  {
    field: "product.fba_priority_queue",
    headerName: "FBA Priority Queue",
    description:
      "Identifies which items are part of today's FBA priority shipment.",
    valueGetter: (value, row) => row.product?.fba_priority_queue,
  },
];

export const nestedTableColumns: string[] = [
  "supply.sort",
  "supply.upc",
  "supply.html",
  "supply.supply_title",
  "supply.supply_asin",
  "inventory.inventory_shelf",
  "supply_families_view.families",
  "inventory.part_number",
  "inventory_with_formatted_id_view.formatted_inventory_id",
  "inventory.inventory_purchase_recommendation",
  "supply.discontinued_date",
  "supply.studio",
  "supply.ebay_itemid",
  "supply.walsku",
  "supply.restock_status_ftp",
  "supply.sup_lin",
  "supply.listed",
  "supply.ono",
  "inventory.avcg",
  "inventory.ai_pooled",
  "supply.house",
  "inventory.add_date",
  "inventory.sub_date",
  "inventory.inv",
  "supply.dist_inv",
  "supply.dau_inv",
  "supply.dmg_inv_display",
  "amazon_product_pricing_info.buy_box_price",
  "amazon_product_pricing_info.buy_box_info",
  "supply.dtb1",
  "supply.dtb2",
  "supply.dtb3",
  "supply.dtb4",
  "supply.dtb5",
  "supply.dtb6",
  "supply.dtb7",
  "supply.dtb8",
  "supply.alliance",
  "supply.amazon",
  "supply.ccvideo",
  "supply.dealsareus",
  "supply.deepdiscount",
  "supply.discount_entertainment",
  "supply.popmarket",
  "supply.inetvideo",
  "supply.importcds",
  "supply.mila",
  "supply.gruv",
  "supply.wildcard1",
  "supply.wildcard2",
  "supply.rarewaves",
  "supply.target_store",
  "supply.vei",
  "supply.walmart",
  "supply.wowhd",
  "supply.supply_notes",
  "supply_media_view.families",
];
